'use client';

import { useEffect, useRef, useState } from 'react';
import {
    Bold, Italic, Underline, AlignLeft, AlignCenter,
    AlignRight, AlignJustify, List, ListOrdered, Heading2, Heading3,
    Link2, Unlink,
} from 'lucide-react';

// ── Helpers ───────────────────────────────────────────────────────────────────

function exec(command: string, value?: string) {
    document.execCommand(command, false, value);
}

function saveSelection() {
    const sel = window.getSelection();
    return sel && sel.rangeCount > 0 ? sel.getRangeAt(0).cloneRange() : null;
}

function restoreSelection(range: Range | null) {
    if (!range) return;
    const sel = window.getSelection();
    if (sel) { sel.removeAllRanges(); sel.addRange(range); }
}

// ── Toolbar ───────────────────────────────────────────────────────────────────

function ToolbarBtn({ command, value, title, children }: {
    command: string;
    value?: string;
    title: string;
    children: React.ReactNode;
}) {
    return (
        <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); exec(command, value); }}
            title={title}
            className="p-1.5 rounded text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
        >
            {children}
        </button>
    );
}

function Toolbar() {
    const [fontSize, setFontSize] = useState(3);
    const savedRange = useRef<Range | null>(null);

    const changeFontSize = (delta: number) => {
        const next = Math.min(7, Math.max(1, fontSize + delta));
        setFontSize(next);
        restoreSelection(savedRange.current);
        exec('styleWithCSS', 'true');
        exec('fontSize', String(next));
    };

    return (
        <div className="flex flex-wrap items-center gap-0.5 p-2 border-b border-slate-200 bg-slate-50 sticky top-0 z-10">
            <ToolbarBtn command="formatBlock" value="h2" title="Heading 2"><Heading2 className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn command="formatBlock" value="h3" title="Heading 3"><Heading3 className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn command="formatBlock" value="p" title="Paragraph">
                <span className="text-xs font-medium px-0.5">¶</span>
            </ToolbarBtn>

            <div className="w-px h-5 bg-slate-200 mx-1" />

            <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); savedRange.current = saveSelection(); changeFontSize(-1); }}
                title="Decrease font size"
                disabled={fontSize <= 1}
                className="p-1.5 rounded text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer disabled:opacity-30 text-sm font-bold"
            >A-</button>
            <span className="text-xs text-slate-400 w-4 text-center">{fontSize}</span>
            <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); savedRange.current = saveSelection(); changeFontSize(1); }}
                title="Increase font size"
                disabled={fontSize >= 7}
                className="p-1.5 rounded text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer disabled:opacity-30 text-sm font-bold"
            >A+</button>

            <div className="w-px h-5 bg-slate-200 mx-1" />

            <ToolbarBtn command="bold" title="Bold"><Bold className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn command="italic" title="Italic"><Italic className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn command="underline" title="Underline"><Underline className="w-4 h-4" /></ToolbarBtn>

            <div className="w-px h-5 bg-slate-200 mx-1" />

            <ToolbarBtn command="justifyLeft" title="Align Left"><AlignLeft className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn command="justifyCenter" title="Align Center"><AlignCenter className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn command="justifyRight" title="Align Right"><AlignRight className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn command="justifyFull" title="Justify"><AlignJustify className="w-4 h-4" /></ToolbarBtn>

            <div className="w-px h-5 bg-slate-200 mx-1" />

            <ToolbarBtn command="insertUnorderedList" title="Bullet List"><List className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn command="insertOrderedList" title="Ordered List"><ListOrdered className="w-4 h-4" /></ToolbarBtn>

            <div className="w-px h-5 bg-slate-200 mx-1" />

            <button
                type="button"
                onMouseDown={(e) => {
                    e.preventDefault();
                    const range = saveSelection();
                    const url = window.prompt('URL');
                    if (url) { restoreSelection(range); exec('createLink', url.startsWith('http') ? url : `https://${url}`); }
                }}
                title="Insert link"
                className="p-1.5 rounded text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
            ><Link2 className="w-4 h-4" /></button>
            <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); exec('unlink'); }}
                title="Remove link"
                className="p-1.5 rounded text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
            ><Unlink className="w-4 h-4" /></button>

            <div className="w-px h-5 bg-slate-200 mx-1" />

            <label title="Text color" className="cursor-pointer flex items-center gap-1 text-slate-600 hover:bg-slate-100 p-1.5 rounded transition-colors">
                <span className="text-xs font-medium">A</span>
                <input
                    type="color"
                    defaultValue="#000000"
                    className="w-5 h-5 rounded cursor-pointer border-0 p-0 bg-transparent"
                    onMouseDown={() => { savedRange.current = saveSelection(); }}
                    onChange={(e) => { restoreSelection(savedRange.current); exec('foreColor', e.target.value); }}
                />
            </label>

            <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); exec('removeFormat'); }}
                title="Remove formatting"
                className="text-xs text-slate-400 hover:text-slate-700 px-1 cursor-pointer"
            >✕</button>
        </div>
    );
}

// ── RichEditor ────────────────────────────────────────────────────────────────

export function RichEditor({
    label,
    initialContent,
    onBlur,
    rounded = true,
    minHeight = 400,
    editorClassName = '',
}: {
    label: string;
    initialContent: string;
    onBlur: (html: string) => void;
    rounded?: boolean;
    minHeight?: number;
    editorClassName?: string;
}) {
    const ref = useRef<HTMLDivElement>(null);
    const [sourceMode, setSourceMode] = useState(false);
    const [sourceHtml, setSourceHtml] = useState('');
    const pendingHtml = useRef<string | null>(null);
    const initialized = useRef(false);

    useEffect(() => {
        if (ref.current && initialContent && !initialized.current) {
            ref.current.innerHTML = initialContent;
            initialized.current = true;
        }
    }, [initialContent]);

    useEffect(() => {
        if (!sourceMode && pendingHtml.current !== null && ref.current) {
            ref.current.innerHTML = pendingHtml.current;
            pendingHtml.current = null;
        }
    }, [sourceMode]);

    useEffect(() => {
        if (sourceMode) onBlur(sourceHtml);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sourceHtml]);

    const enterSource = () => {
        const html = ref.current ? ref.current.innerHTML : initialContent;
        setSourceHtml(html);
        setSourceMode(true);
    };

    const exitSource = () => {
        pendingHtml.current = sourceHtml;
        onBlur(sourceHtml);
        setSourceMode(false);
    };

    const containerClass = `border border-slate-200 overflow-hidden flex flex-col flex-1${rounded ? ' rounded-lg' : ''}`;

    return (
        <div className="flex flex-col flex-1 min-w-0">
            {label && (
                <span className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-2">{label}</span>
            )}
            <div className={containerClass}>
                <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50">
                    {sourceMode ? (
                        <div className="flex items-center gap-2 px-2 py-1.5">
                            <span className="text-xs text-slate-400 font-mono">HTML source</span>
                        </div>
                    ) : (
                        <Toolbar />
                    )}
                    <button
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); sourceMode ? exitSource() : enterSource(); }}
                        title={sourceMode ? 'Back to editor' : 'Edit HTML source'}
                        className={`px-2 py-1.5 text-xs font-mono font-medium transition-colors cursor-pointer shrink-0 ${sourceMode ? 'bg-slate-800 text-white' : 'text-slate-500 hover:bg-slate-100'}`}
                    >
                        {'</>'}
                    </button>
                </div>
                {sourceMode ? (
                    <textarea
                        value={sourceHtml}
                        onChange={(e) => setSourceHtml(e.target.value)}
                        className="flex-1 p-4 font-mono text-xs text-slate-700 outline-none resize-none bg-white"
                        style={{ minHeight }}
                        spellCheck={false}
                    />
                ) : (
                    <div
                        ref={ref}
                        contentEditable
                        suppressContentEditableWarning
                        onFocus={() => exec('defaultParagraphSeparator', 'p')}
                        onInput={() => ref.current && onBlur(ref.current.innerHTML)}
                        onBlur={() => ref.current && onBlur(ref.current.innerHTML)}
                        onPaste={(e) => {
                            const text = e.clipboardData.getData('text/plain').trim();
                            if (/^https?:\/\/\S+$/i.test(text)) {
                                e.preventDefault();
                                const sel = window.getSelection();
                                if (sel && sel.rangeCount > 0 && !sel.isCollapsed) {
                                    exec('createLink', text);
                                } else {
                                    exec('insertHTML', `<a href="${text}" target="_blank" rel="noopener noreferrer">${text}</a>`);
                                }
                            }
                        }}
                        className={`flex-1 p-4 about-content outline-none ${editorClassName}`}
                        style={{ minHeight }}
                    />
                )}
            </div>
        </div>
    );
}
